import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PlaceAutocompleteInput from './Supplier/PlaceAutocompleteInput';
import useThemeStyles from '../theme/useThemeStyles';

// Updated steps - added Wedding Vibe & Budget step
const steps = ['Basic Details', 'Partner Information', 'Wedding Vibe & Budget', 'Getting Started'];

// Wedding style options
const WEDDING_TYPES = [
  'luxury', 'traditional', 'destination', 'micro-wedding', 'elopement', 'diy', 'intimate', 'outdoor', 'indoor', 'garden', 'beach', 'barn', 'urban', 'other'
];

const VIBE_TAGS = [
  'romantic', 'minimal', 'garden', 'editorial', 'festival', 'rustic',
  'elegant', 'bohemian', 'vintage', 'modern', 'classic', 'glamorous',
  'whimsical', 'industrial', 'cozy', 'sophisticated', 'playful', 'timeless', 'eclectic', 'natural'
];

const FORMALITY_OPTIONS = [
  { value: 'very-formal', label: 'Very Formal' },
  { value: 'formal', label: 'Formal' },
  { value: 'semi-formal', label: 'Semi-Formal' },
  { value: 'casual', label: 'Casual' },
  { value: 'cocktail', label: 'Cocktail' }
];

const animatedEntrySx = (delay = 0) => ({
  opacity: 0,
  transform: 'translateY(10px)',
  animation: `fadeInUp 500ms ease-out ${delay}ms forwards`,
  '@keyframes fadeInUp': {
    from: {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
});

function CreateWedding() {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setWedding } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [weddingData, setWeddingData] = useState({
    name: '',
    location: '',
    partnerOneName: '',
    partnerTwoName: '',
    partnerTwoEmail: '',
    // Set default membership level to gold with free trial
    membershipLevel: 'gold',
    // Wedding vibe and budget
    weddingType: [],
    vibeTags: [],
    formality: '',
    initialBudget: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    setWeddingData({
      ...weddingData,
      [field]: event.target.value
    });
    
    // Clear field error when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleValueChange = (field) => (value) => {
    setWeddingData({
      ...weddingData,
      [field]: value
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleToggleTag = (field, value) => {
    const currentArray = weddingData[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setWeddingData({
      ...weddingData,
      [field]: newArray
    });
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!weddingData.name.trim()) {
        newErrors.name = 'Wedding name is required';
      }
    } else if (activeStep === 1) {
      if (!weddingData.partnerOneName.trim()) {
        newErrors.partnerOneName = 'Partner name is required';
      }
      if (!weddingData.partnerTwoName.trim()) {
        newErrors.partnerTwoName = 'Partner name is required';
      }
    } else if (activeStep === 2) {
      // Wedding Vibe & Budget step - budget is required
      if (!weddingData.initialBudget || parseFloat(weddingData.initialBudget) <= 0) {
        newErrors.initialBudget = 'Please enter your initial budget';
      }
    }
    // No validation for step 3 (Getting Started) as it's just informational
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep((prevStep) => prevStep + 1);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Format the data for the API
      const formattedData = {
        ...weddingData,
      };
      
      const _response = await api.post('/api/wedding', formattedData);
      
      setSuccess('Wedding created successfully!');
      
      // Fetch the complete wedding data to update the context
      try {
        const weddingResponse = await api.get('/api/wedding');
        
        // Update the wedding in the AuthContext
        setWedding(weddingResponse.data);
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/supplier-search');
        }, 1500);
      } catch (fetchError) {
        // Still redirect to dashboard, the context will be updated on next page load
        setTimeout(() => {
          navigate('/supplier-search');
        }, 1500);
      }
      
    } catch (error) {
      // Log more detailed error information
      if (error.response) {
        setError(error.response.data.message || 'Failed to create wedding. Please try again.');
      } else if (error.request) {
        setError('No response received from server. Please check your connection and try again.');
      } else {
        setError('An error occurred while creating your wedding. Please try again.');
      }

      setLoading(false);
    }
  };

  const renderStepContent = () => {
    // If we have the "already has a wedding" error, show a special step
    if (activeStep === steps.length) {
      return (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/dashboard')}
            sx={{ mt: 2 }}
          >
            Go to Your Wedding Dashboard
          </Button>
        </Box>
      );
    }
    
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 4 }}>
            <TextField
              fullWidth
              label="Wedding Name"
              value={weddingData.name}
              onChange={handleChange('name')}
              margin="normal"
              error={!!errors.name}
              helperText={errors.name}
              placeholder="e.g., Sarah & John's Wedding"
              required
            />
            <Box sx={{ mt: 2 }}>
              <PlaceAutocompleteInput
                value={weddingData.location}
                onChange={handleValueChange('location')}
                onSelect={(place) => {
                  if (place?.address) {
                    handleValueChange('location')(place.address);
                  }
                }}
                label="Wedding Location"
                placeholder="Search city or town (e.g., New York)"
                error={!!errors.location}
                helperText={errors.location || 'Optional - select a city/town or add this later'}
              />
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Partner 1 (You)
            </Typography>
            <TextField
              fullWidth
              label="Partner One's name"
              value={weddingData.partnerOneName}
              onChange={handleChange('partnerOneName')}
              margin="normal"
              error={!!errors.partnerOneName}
              helperText={errors.partnerOneName}
              placeholder="Partner One's name"
              required
            />
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Partner 2
            </Typography>
            <TextField
              fullWidth
              label="Partner Two's name"
              value={weddingData.partnerTwoName}
              onChange={handleChange('partnerTwoName')}
              margin="normal"
              error={!!errors.partnerTwoName}
              helperText={errors.partnerTwoName}
              placeholder="Partner Two's name"
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={weddingData.partnerTwoEmail}
              onChange={handleChange('partnerTwoEmail')}
              margin="normal"
              placeholder="Optional - to send an invitation"
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Tell Us About Your Wedding Style & Budget
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              This helps us match you with the perfect suppliers for your wedding.
            </Typography>

            {/* Wedding Type */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Wedding Type (select all that apply)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {WEDDING_TYPES.map((type) => (
                  <Chip
                    key={type}
                    label={type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' ')}
                    onClick={() => handleToggleTag('weddingType', type)}
                    color={weddingData.weddingType?.includes(type) ? 'primary' : 'default'}
                    variant={weddingData.weddingType?.includes(type) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>

            {/* Vibe Tags */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Wedding Vibe (select all that apply)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {VIBE_TAGS.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                    onClick={() => handleToggleTag('vibeTags', tag)}
                    color={weddingData.vibeTags?.includes(tag) ? 'primary' : 'default'}
                    variant={weddingData.vibeTags?.includes(tag) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>

            {/* Formality */}
            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Formality Level</InputLabel>
                <Select
                  value={weddingData.formality || ''}
                  onChange={handleChange('formality')}
                  label="Formality Level"
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {FORMALITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Initial Budget */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Initial Budget"
                type="number"
                value={weddingData.initialBudget}
                onChange={handleChange('initialBudget')}
                error={!!errors.initialBudget}
                helperText={errors.initialBudget || 'This will create your "Initial Budget" pot'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  step: 100
                }}
              />
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 1,
                fontSize: { xs: '2rem', sm: '2.5rem' },
                color: 'text.primary',
                ...animatedEntrySx(80)
              }}
            >
              It's Official 🎉
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.primary', ...animatedEntrySx(140) }}>
              This is where your plans come together.
            </Typography>

            <Box sx={{ ...animatedEntrySx(200), mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleNext}
                disabled={loading}
                sx={{ px: 4, py: 1.25, minWidth: 260 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Start Exploring Suppliers'}
              </Button>
            </Box>

          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
      
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, textAlign: 'center' }}>
          {activeStep === steps.length - 1 ? "You're All Set" : 'Create Your Wedding'}
        </Typography>
        
        {isMobile ? (
          <Typography variant="subtitle1" sx={{ mt: 4, fontWeight: 500, color: 'black', textAlign: 'center' }}>
            Step {activeStep + 1} of {steps.length}: {steps[activeStep]}
          </Typography>
        ) : (
          <Stepper activeStep={activeStep} sx={{ mt: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        
        {renderStepContent()}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          {activeStep === 0 ? <Box /> : (
            <Button
              disabled={loading}
              onClick={handleBack}
              variant="outlined"
              color="primary"
            >
              Back
            </Button>
          )}
          {activeStep !== steps.length - 1 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Next'
              )}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default CreateWedding; 